/**
 * "Something is broken" — the incident path on `/tickets/new`.
 *
 * A thin owner around `IncidentReporter`, which holds the questions and the
 * follow-up step shared with the assistant's inline card. Filing here goes
 * through `useTicketSubmit`: idempotent across double presses, then straight
 * to the new ticket with its number in a toast.
 *
 * Raised for a colleague, there is no ticket to go to — it is theirs, not in
 * the reporter's My tickets — so the page says so instead: "Raised for <name>
 * — they'll get the updates. Ticket number …".
 *
 * Added to someone else's open ticket instead ("Is this the same problem?" ->
 * "Yes, add me to it", 8 Oct), nothing was filed: the page says so, with the
 * same two ways on.
 */

import { Alert, Button } from "@norican/react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { useT } from "@/i18n";

import { useTicketSubmit } from "../lib/use-ticket-submit";
import { SUBJECT_MAX_LENGTH } from "../lib/validation";
import { IncidentReporter } from "./incident-reporter";
import { type JoinedTicket, SimilarJoinedNotice } from "./similar-ticket-prompt";

export interface IncidentFormProps {
  /**
   * "Report a problem" on a finished ticket (9 Oct): the ticket this follows.
   * Sent as `follow_up_of`; the server names it in the description and gives
   * the incident to that ticket's owner and team.
   */
  followUp?: { ticketId: string; subject: string } | null;
}

export function IncidentForm({ followUp = null }: IncidentFormProps) {
  const { t } = useT("tickets");
  const navigate = useNavigate();
  const { submit, isPending, error, raisedFor, reset } = useTicketSubmit();
  /** Remounts the reporter blank for "Report something else". */
  const [attempt, setAttempt] = useState(0);
  const [joined, setJoined] = useState<JoinedTicket | null>(null);

  if (raisedFor !== null || joined !== null) {
    return (
      <div className="flex flex-col gap-4">
        {joined !== null ? (
          <SimilarJoinedNotice joined={joined} />
        ) : raisedFor === null ? null : (
          <Alert tone="success" title={t("onBehalf.raisedTitle", { name: raisedFor.name })}>
            {t("onBehalf.raised", { name: raisedFor.name, number: raisedFor.number })}{" "}
            {t("onBehalf.notInList")}
          </Alert>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => {
              reset();
              setJoined(null);
              setAttempt((value) => value + 1);
            }}
          >
            {t("onBehalf.reportAnother")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            tone="neutral"
            onClick={() => void navigate("/tickets")}
          >
            {t("onBehalf.myTickets")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <IncidentReporter
      key={attempt}
      initial={
        followUp === null
          ? undefined
          : {
              subject: t("reportProblem.subject", { subject: followUp.subject }).slice(
                0,
                SUBJECT_MAX_LENGTH,
              ),
            }
      }
      submitting={isPending}
      submitError={error}
      onSubmit={
        followUp === null
          ? submit
          : (body, fingerprint, forPerson) =>
              submit(
                // A report for a colleague is theirs, not a follow-up of this ticket.
                forPerson === null ? { ...body, follow_up_of: followUp.ticketId } : body,
                fingerprint,
                forPerson,
              )
      }
      onCancel={() => void navigate("/tickets")}
      onJoined={setJoined}
    />
  );
}
